import type { ReactNode } from "react";

/**
 * Column data type. Drives default sorting/filtering operators, default
 * cell formatters, and which filter inputs render in the filter bar.
 *
 * - `text` — string content; collation-aware sort.
 * - `number` — generic numeric value.
 * - `money` — numeric value formatted as currency. Set `currency` on the column.
 * - `percent` — numeric value rendered as percent (0.25 -> "25%").
 * - `date` — calendar date only.
 * - `datetime` — date + time.
 * - `enum` — bounded set of values; `enumLabels` maps value -> display label.
 * - `boolean` — true/false.
 * - `relation` — foreign key reference; `relationLabel` resolves the display.
 * - `custom` — fully caller-controlled rendering via `cell`.
 */
export type ColumnType =
  | "text"
  | "number"
  | "money"
  | "percent"
  | "date"
  | "datetime"
  | "enum"
  | "boolean"
  | "relation"
  | "custom";

/** Horizontal alignment of cell content. */
export type ColumnAlign = "left" | "center" | "right";

/** Column pin side for sticky columns. */
export type ColumnSticky = "left" | "right";

/**
 * Argument passed to a column's optional custom `cell` renderer.
 */
export interface CellRenderArgs<TRow> {
  row: TRow;
  value: unknown;
  column: ColumnDef<TRow>;
}

/**
 * Argument passed to a column's optional custom `format` function.
 *
 * Distinct from `cell` because `format` returns a plain string used by
 * exporters and the default cell renderer; `cell` returns ReactNode.
 */
export interface FormatArgs<TRow> {
  row: TRow;
  value: unknown;
  column: ColumnDef<TRow>;
  locale: string;
}

/**
 * Declarative column definition. Used both client- and server-side.
 *
 * - `header` may be a literal string OR an i18n key (resolved by the table
 *   shell at render time; nothing in this file resolves keys).
 * - `accessor` defaults to `(row) => (row as Record<string, unknown>)[key]`.
 * - `cell` overrides default rendering for one column.
 * - `format` overrides the export/text representation for one column.
 */
export interface ColumnDef<TRow> {
  /** Stable key used for state (sort, filter, visibility, sizing). */
  key: string;
  /** Display label OR i18n key. */
  header: string;
  /** Column data type for default formatting/operators. */
  type: ColumnType;
  /** Custom value extractor; defaults to `row[key]`. */
  accessor?: (row: TRow) => unknown;
  /** Default column width in px. */
  width?: number;
  /** Minimum column width in px when resized. */
  minWidth?: number;
  /** Maximum column width in px when resized. */
  maxWidth?: number;
  /** Whether the column can be sorted. */
  sortable?: boolean;
  /** Whether the column appears in the filter bar dropdown. */
  filterable?: boolean;
  /** Pin to a side; otherwise the column scrolls horizontally. */
  sticky?: ColumnSticky;
  /** Horizontal alignment for cells AND header. Numeric types default `right`. */
  align?: ColumnAlign;
  /**
   * Required permission to view this column. The table reads this but DOES
   * NOT enforce — RBAC enforcement is wired in a later phase.
   */
  requiredPermission?: string;
  /** Currency code for `type: "money"`. Defaults to "EUR" in formatters. */
  currency?: string;
  /** Display labels for `type: "enum"` keyed by raw value. */
  enumLabels?: Record<string, string>;
  /** Display resolver for `type: "relation"`. */
  relationLabel?: (value: unknown) => string;
  /** Optional custom React cell renderer; overrides default formatting. */
  cell?: (args: CellRenderArgs<TRow>) => ReactNode;
  /** Optional custom string formatter; used by exporters AND the default cell. */
  format?: (args: FormatArgs<TRow>) => string;
}

/**
 * Identity helper. Exists purely so callers get inferred row type without
 * having to spell `ColumnDef<Invoice>[]` at every site.
 *
 * @example
 *   const cols = defineColumns<Invoice>([
 *     { key: "number", header: "Number", type: "text" },
 *     { key: "total",  header: "Total",  type: "money" },
 *   ]);
 */
export function defineColumns<TRow>(cols: ColumnDef<TRow>[]): ColumnDef<TRow>[] {
  return cols;
}

/**
 * Resolve a column's accessor, falling back to `row[column.key]` when the
 * column does not declare one explicitly.
 */
export function resolveAccessor<TRow>(column: ColumnDef<TRow>): (row: TRow) => unknown {
  if (column.accessor) return column.accessor;
  const key = column.key;
  return (row) => (row as Record<string, unknown>)[key];
}
